import _ from "lodash";
import promiseError from "libs/promiseError";
import { formatBands } from "helpers/Bands";
import {
  fillUploadedDocsArray,
  supportDocsCount,
  fillReportsArray,
  testReportsCount
} from "helpers/Uploads";
import {
  isSaved,
  isApproved,
  getRequestStatuses,
  isEco,
  isInitial,
  hasUsersManualApproved,
  getRequestTypeTitle
} from "helpers/RequestHelper";
import { sortPocs } from "helpers/SortHelper";
import { hasCBSDDPtype } from "helpers/DeviceTypeHelper";
import { isCompleteString } from "helpers/RequestTestsHelper";
import { getFullName } from "helpers/Users";
import { formatBillingAddress } from "helpers/BillingHelper";
import * as actions from "actions/types";

const fetchRequest = (action, state) => {
  const { payload } = action;
  const { request, data } = payload;

  if (payload.status === 200) {
    if (data) {
      const reqData = data.data;
      const {
        devicemodel,
        poc,
        submitter,
        requeststatusid,
        requesttypeid,
        billing,
        invoice
      } = reqData;
      const reqStatuses = getRequestStatuses(reqData);
      const manualApproved =
        state && state.usersManual
          ? hasUsersManualApproved(state.usersManual)
          : false;

      return {
        ...state,
        ...reqStatuses,
        _requestOriginal: reqData,
        agreementAccepted: !isSaved(requeststatusid),
        bands: formatBands(devicemodel.devicemodelbands),
        billingId: reqData.billingid || 0,
        billingPocId: billing.pocid || 0,
        billingAddressId: billing.addressid || 0,
        billingOrderNum: reqData.purchaseorder,
        billingPocName: getFullName(billing.poc),
        billingPocPhone: billing.poc ? billing.poc.phone : "",
        billingPocEmail: billing.poc ? billing.poc.email : "",
        billingAddress: formatBillingAddress(billing.address),
        billingCompanyName: billing.address ? billing.address.name : "",
        cdmaLab: reqData.primarylabid,
        certificationOption: reqData.publishdetails,
        completeDate: reqData.completedat,
        descriptionOfChange: reqData.descriptionofchange,
        deviceType: devicemodel.devicetype.id ? devicemodel.devicetype.id : 13, // TODO: get deviceType from parent model
        dpSwVersion: reqData.dpswversion.trim(),
        fccId: devicemodel.fccid,
        hasBilling: billing !== false,
        hasUsersManualApproved: manualApproved,
        hasParentApproved:
          reqData.parent && isApproved(reqData.parent.requeststatusid),
        hasTestReportsApproved: isCompleteString(reqData.testingstatus),
        hwVersion: reqData.hwversion.trim(),
        id: reqData.id,
        invoice,
        isCBSDDPdevice: hasCBSDDPtype(devicemodel.devicetype.id),
        isEco: isEco(requesttypeid),
        isInitial: isInitial(requesttypeid),
        isPaid: Boolean(invoice.paid),
        isTogglerEnabled: false,
        isValid: true,
        model: devicemodel,
        modelName: devicemodel.modelnumber,
        needUpdate: false,
        parent: reqData.parent,
        payCheckNumber: invoice.checknumber,
        payDate: invoice.paidat,
        payMethod: invoice.paymentmethod,
        payNote: invoice.paymentnote,
        pocId: poc.id,
        pocFirstName: poc.firstname,
        pocLastName: poc.lastname,
        pocEmail: poc.email,
        pocPhone: poc.phone,
        pocFullName: `${poc.firstname} ${poc.lastname}`,
        saveDate: reqData.savedat,
        status: requeststatusid,
        submissionDate: reqData.pendingat,
        submitter,
        submitterId: reqData.submitteruserid,
        supportDocs: fillUploadedDocsArray(
          supportDocsCount,
          reqData.requestdocuments
        ),
        swVersion: reqData.swversion.trim(),
        testResults: fillReportsArray(),
        testStatus: reqData.testingstatus,
        tn: reqData.formatedtransactionnumber,
        type: requesttypeid,
        typeTitle: getRequestTypeTitle(requesttypeid),
        vendor: devicemodel.vendor.name
      };
    }
  } else {
    const fetchError = promiseError(request);
    return { ...state, fetchError };
  }
};

const createDeviceModel = (action, state) => {
  const { payload } = action;
  const { status, data } = payload;

  if (status === 200) {
    return { ...state, deviceModelId: data.id };
  }

  return state;
};

const updateDeviceModel = (action, state) => {
  const { payload } = action;
  const { status } = payload;

  if (status === 200) {
    return { ...state, needUpdate: true };
  }

  return state;
};

const fetchPocs = (action, state) => {
  const pocs = action.payload;
  if (_.isEmpty(pocs)) return { ...state, pocs: [] };

  return { ...state, pocs: sortPocs(pocs) };
};

// const updateRequest = (action, state) => {
//   const { payload } = action;
//   const { status } = payload;

//   if (status === 200) {
//     return { ...state, needUpdate: true };
//   }

//   return state;
// };

const getUsersManualInfo = (action, state) => {
  const { payload } = action;
  const { status, data } = payload;

  if (status === 200) {
    const usersManual = data.data || undefined;
    const usersManualApproved = hasUsersManualApproved(usersManual);
    return {
      ...state,
      usersManual,
      hasUsersManualApproved: usersManualApproved
    };
  }

  return state;
};

const updateUsersManual = (action, state) => {
  const { payload } = action;
  const { status, data } = payload;

  if (status === 200) {
    const manual = { ...state.usersManual };
    const manualDoc = data.data || undefined;
    manual.document = manualDoc;

    const usersManualApproved = hasUsersManualApproved(manual);
    return {
      ...state,
      usersManual: manual,
      hasUsersManualApproved: usersManualApproved
    };
  }

  return state;
};

const fetchReports = (action, state) => {
  if (!action.payload.data) return state;

  const testResults = fillUploadedDocsArray(
    testReportsCount,
    action.payload.data.data
  );
  return { ...state, testResults };
};

const updateStatus = (action, state) => {
  if (!action.payload.data) return state;
  return {
    ...state,
    requeststatusid: action.payload.data.data.requeststatusid
  };
};

const setValidationStatus = (action, state) => {
  return {
    ...state,
    isValid: action.payload
  };
};

export default function (state = null, action) {
  switch (action.type) {
    case actions.FETCH_REQUEST:
      return fetchRequest(action, state);
    case actions.NEW_REQUEST_INIT:
      return action.payload;
    case actions.CHANGE_REQUEST_FIELD:
      return { ...state, [action.payload.field]: action.payload.value };
    case actions.SAVE_REQUEST:
      return { ...state, ...action.payload };
    case actions.CREATE_DEVICE_MODEL:
      return createDeviceModel(action, state);
    case actions.CLEAR_REQUEST:
      return null;
    case actions.FETCH_POCS:
      return fetchPocs(action, state);
    case actions.CREATE_NEW_POC:
      return { ...state, pocId: action.payload };
    case actions.CREATE_NEW_BILLING_POC:
      return { ...state, billingPocId: action.payload };
    case actions.CREATE_NEW_BILLING_ADDRESS:
      return { ...state, billingAddressId: action.payload };
    case actions.UPDATE_DEVICE_MODEL:
      return updateDeviceModel(action, state);
    case actions.UPDATE_REQUEST:
      return { ...state, needUpdate: true };
    case actions.GET_USERS_MANUAL_INFO:
      return getUsersManualInfo(action, state);
    case actions.SIGN_AGREEMENT:
      return { ...state, needUpdate: true };
    case actions.CLEAN_POC_INFO:
    case actions.RETURN_TO_EDIT_REQUEST:
      return { ...state, ...action.payload };
    case actions.SUBMIT_INITIAL_REQUEST:
    case actions.SUBMIT_ECO_REQUEST:
      return { ...state, ...action.payload };
    case actions.FETCH_TEST_REPORTS:
      return fetchReports(action, state);
    case actions.UPDATE_USERS_MANUAL:
      return updateUsersManual(action, state);
    case actions.UPDATE_REQUEST_STATUS:
      return updateStatus(action, state);
    case actions.SET_VALIDATION_STATUS:
      return setValidationStatus(action, state);
    case actions.TOGGLE_REQUEST_EDIT_MODE:
      return {
        ...state,
        isTogglerEnabled: !state.isTogglerEnabled
      };
    case actions.FETCH_BILLING_ADDRESSES:
      return { ...state, billingAddresses: action.payload };
    case actions.FETCH_REQUEST_BILLING_INFO:
      const { pocid, addressid } = action.payload;
      return { ...state, billingPocId: pocid, billingAddressId: addressid };
    case actions.CLEAR_COUNTRY_STATE:
      return { ...state, billingAddressState: "" };
    case actions.UPDATE_BILLING_INFO:
      return {
        ...state,
        billingAddressId: action.payload.addressid,
        billingPocId: action.payload.pocid,
        billingId: action.payload.id
      };
    case actions.UPDATE_PAYMENT:
      const invoice = { ...state.invoice, ...action.payload, paid: 1 };
      return {
        ...state,
        invoice,
        isPaid: true,
        payCheckNumber: action.payload.checknumber
      };
    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/reducers/request.js